import React, { useEffect, useState } from "react";
import {
  Modal,
  Calendar,
  Input,
  Button,
  Typography,
  Row,
  Col,
  DatePicker,
  AutoComplete,
  App,
  Upload,
  message,
  Table,
  Select,
  Card,
  Statistic,
  Tag,
  Divider,
} from "antd";
import { PaperClipOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "./leaveModal.css";
import UnmarkedAttendanceAlert from "../../../Employees/components/UnmarkedAttendanceAlert";

const { Text } = Typography;

const LeaveModal = ({
  visible,
  onClose,
  onLeaveSubmit,
  restrictedHolidays = [],
  weekEndDays = [],
  totalLeaveTaken = 0,
  leaveRemainingBalance = 0,
  leaveDetailsSummary,
  isDisabled,
  isProbation = false,
  isIndia = false,
}) => {
  const { modal } = App.useApp();
  const [dateTime, setDateTime] = useState(dayjs());
  const [leaveType, setLeaveType] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [notes, setNotes] = useState("");
  const [fileList, setFileList] = useState([]);  // ← NEW

  // ⏰ Live clock
  useEffect(() => {
    const timer = setInterval(() => setDateTime(dayjs()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 🧹 Reset on close
  useEffect(() => {
    if (!visible) {
      setLeaveType(null);
      setFromDate(null);
      setToDate(null);
      setNotes("");
      setFileList([]);  // ← NEW
    }
  }, [visible]);

  const leaveTypes = [
    { label: "Sick Leave", value: "sick_leave" },
    // Annual Leave and Casual Leave are the same pool — only the
    // country-appropriate label/value is offered, never both.
    isIndia
      ? { label: "Casual Leave", value: "casual_leave" }
      : { label: "Annual Leave", value: "annual_leave" },
    {
      label: isProbation ? "Flexi Leave (available after probation)" : "Flexi Leave",
      value: "flexi_leave",
      disabled: isProbation,
    },
    { label: "Emergency Leave (Unpaid)", value: "emergency_leave" },
    { label: "Compensatory Leave", value: "compensatory_leave" },
    { label: "Maternity Leave", value: "maternity_leave" },
  ];

  // Company-configured window (Company Profile → HR), delivered with the
  // leave summary; the server enforces the same limit. Before a type is
  // picked, allow the widest window so the picker isn't needlessly narrow.
  const generalBackdateLimit = leaveDetailsSummary?.backdateLimits?.general ?? 7;
  const sickBackdateLimit = leaveDetailsSummary?.backdateLimits?.sick ?? 7;
  const ALLOWED_BACKDATE_DAYS = !leaveType
    ? Math.max(generalBackdateLimit, sickBackdateLimit)
    : leaveType.value === "sick_leave"
      ? sickBackdateLimit
      : generalBackdateLimit;

  // 🚫 Disable restricted, past, or weekend dates
  // const disableDate = (current) => {
  //   const restrictedDates = Array.isArray(restrictedHolidays)
  //     ? restrictedHolidays.map((holiday) => holiday?.restricted_holiday_date)
  //     : [];
  //   const weekends = Array.isArray(weekEndDays) ? weekEndDays : [];
  //   return (
  //     restrictedDates.includes(current.format("YYYY-MM-DD")) ||
  //     current.isBefore(dayjs(), "day") ||
  //     weekends.includes(current.day())
  //   );
  // };
  const disableDate = (current) => {
  const restrictedDates = Array.isArray(restrictedHolidays)
    ? restrictedHolidays.map((holiday) => holiday?.restricted_holiday_date)
    : [];

  const weekends = Array.isArray(weekEndDays)
    ? weekEndDays
    : [];

  return (
    restrictedDates.includes(current.format("YYYY-MM-DD")) ||
    current.isBefore(
      dayjs().subtract(ALLOWED_BACKDATE_DAYS, "day").startOf("day")
    ) ||
    weekends.includes(current.day())
  );
};

  // ── File upload validation ──
  const beforeUpload = (file) => {
    const allowed = ["image/jpeg", "image/png", "application/pdf"];
    const under5MB = file.size / 1024 / 1024 < 5;
    if (!allowed.includes(file.type)) {
      message.error("Only JPG, PNG, or PDF files are allowed.");
      return Upload.LIST_IGNORE;
    }
    if (!under5MB) {
      message.error("File must be under 5MB.");
      return Upload.LIST_IGNORE;
    }
    setFileList((prev) => [...prev, file]);
    return false; // prevent auto-upload
  };

  // 🧠 Handle Save
  const handleSave = () => {
    if (!leaveType)
      return modal.warning({ title: "Select Leave Type", centered: true });
    if (!fromDate)
      return modal.warning({ title: "Select From Date", centered: true });
    if (!toDate)
      return modal.warning({ title: "Select To Date", centered: true });
    if (!notes?.trim())
      return modal.warning({ title: "Notes is required", centered: true });

    const formattedFromDate = dayjs(fromDate).format("YYYY-MM-DD");
    const formattedToDate   = dayjs(toDate).format("YYYY-MM-DD");

    if (formattedFromDate > formattedToDate)
      return modal.warning({
        title: "The 'From' date cannot be later than the 'To' date.",
        centered: true,
      });

    onLeaveSubmit({
      leaveType,
      from_date:   formattedFromDate,
      to_date:     formattedToDate,
      notes,
      attachments: fileList,  // ← NEW
    });
  };


  // Leave Table Columns
  const leaveColumns = [
    {
      title: "Leave Type",
      dataIndex: "leave_type",
      key: "leave_type",
      align:"center",
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      align:"center",
      render:value=>
        value!==undefined?
        value:
        <Text type="secondary">N/A</Text>
    },
    {
      title: "Used",
      dataIndex: "used",
      key: "used",
      align:"center",
      render:value=>
        <Tag color="blue">
            {value}
        </Tag>
    },
    {
      title: "Pending",
      dataIndex: "pending",
      key: "pending",
      align:"center",
      render:value=>
        value?
        <Tag color="orange">
            {value}
        </Tag>
        :
        <Tag>
            0
        </Tag>
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      align:"center",
      render:value=>
        value!==undefined?
        <Tag color="green">
            {value}
        </Tag>
        :
        <Text type="secondary">
            N/A
        </Text>,
    },
  ];

  const selectedLeaveSummary =
    leaveDetailsSummary?.leaveSummary?.find(
        item => item.key === leaveType?.value
    ) || {};


  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={850}
      centered
      className="leave-modal"
    >
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginTop: "1rem" }}>
        <Col>
          <Text strong className="attendance-modal-title">Leave</Text>
        </Col>
        <Col>
          <Text className="attendance-modal-time">
            {dateTime.format("DD-MMM-YYYY hh:mm:ss A")}
          </Text>
        </Col>
      </Row>

      {/* Calendar */}
      <div
        className="attendanceOverview-calendar-container leave-calendar-container"
        style={{ marginTop: "2rem" }}
      >
        <div className="leave-summary-header">
            <div>
                <div className="leave-summary-title">
                    Leave Balance Summary
                </div>
                <div className="leave-summary-subtitle">
                    Current leave usage and remaining balance.
                </div>
            </div>
        </div>
        <Table
          rowKey="key"
          size="middle"
          pagination={false}
          columns={leaveColumns}
          dataSource={leaveDetailsSummary?.leaveSummary || []}
        />
        {/* <Calendar fullscreen={false} disabledDate={disableDate} /> */}
      </div>

      <UnmarkedAttendanceAlert
        unmarkedAttendance={leaveDetailsSummary?.unmarkedAttendance}
        style={{ marginTop: 16 }}
      />

      <Divider orientation="center" style={{ marginTop:"2rem" }} >
          Leave Application
      </Divider>

      {/* Leave Type + Balance */}
      <Row gutter={16} style={{ marginTop: "2rem" }}>
        <Col xs={24} md={12}>
          <label className="form-label">
            Leave Type <span className="form-span">*</span>
          </label>
          {/* <AutoComplete
            style={{ width: "100%" }}
            options={leaveTypes.map((item) => ({
              label: item.label,
              value: item.value,
            }))}
            value={leaveType?.label}
            onSelect={(value, option) => {
              setLeaveType({ label: option.label, value: option.value });
              // clear attachments if switching away from sick leave
              if (option.value !== "sick_leave") setFileList([]);
            }}
            placeholder="Select Leave Type"
            data-testid="leave_type"
          /> */}
          <Select
            style={{ width: "100%" }}
            placeholder="Select Leave Type"
            data-testid="leave_type"
            value={leaveType?.value}
            options={leaveTypes.map(item => ({
                label: item.label,
                value: item.value,
                disabled: item.disabled,
            }))}
            onChange={(value, option) => {
                setLeaveType({
                    value,
                    label: option.label,
                });

                if (value !== "sick_leave") {
                    setFileList([]);
                }
            }}
        />

        </Col>

        {/* <Col xs={24} sm={12} style={{ display: "flex", gap: "1rem" }}>
          <div style={{ flex: 1 }}>
            <label className="form-label">No. of Days</label>
            <div className="value-box">{totalLeaveTaken}</div>
          </div>
          <div style={{ flex: 1 }}>
            <label className="form-label">Remaining Balance</label>
            <div className="value-box">{leaveRemainingBalance}</div>
          </div>
        </Col> */}

        <Col xs={24} md={12}>
          <Row gutter={12}>

              <Col span={12}>
                  <Card size="small">
                      <Statistic
                          title="Used"
                          value={selectedLeaveSummary.used ?? 0}
                      />
                  </Card>
              </Col>

              <Col span={12}>
                  <Card size="small">
                      <Statistic
                          title="Remaining"
                          value={
                              selectedLeaveSummary.balance ??
                              leaveRemainingBalance
                          }
                          valueStyle={{
                              color:"#52c41a"
                          }}
                      />
                  </Card>
              </Col>

          </Row>
      </Col>
      </Row>

      {/* Dates */}
      <Row gutter={16} style={{ marginTop: "1.5rem" }}>
        <Col xs={24} sm={12}>
          <label className="form-label">
            From Date <span className="form-span">*</span>
          </label>
          <DatePicker
            style={{ width: "100%" }}
            value={fromDate}
            onChange={(val) => setFromDate(val)}
            disabledDate={disableDate}
            format="DD-MMM-YYYY"
            data-testid="leave_from_date"
          />
        </Col>
        <Col xs={24} sm={12}>
          <label className="form-label">
            To Date <span className="form-span">*</span>
          </label>
          <DatePicker
            style={{ width: "100%" }}
            value={toDate}
            onChange={(val) => setToDate(val)}
            disabledDate={disableDate}
            format="DD-MMM-YYYY"
            data-testid="leave_to_date"
          />
        </Col>
      </Row>

      {/* Supporting document -- available for any leave type, not just Sick
          Leave, so HR can review proof for Compensatory/Maternity/etc too. */}
      {leaveType && (
        <div style={{ marginTop: "1.5rem" }}>
          <label className="form-label">
            {leaveType.value === "sick_leave" ? "Medical Certificate" : "Supporting Document"}{" "}
            <span style={{ color: "#888", fontSize: 12 }}>(Optional · max 3)</span>
          </label>
          <Upload
            fileList={fileList}
            maxCount={3}
            multiple
            beforeUpload={beforeUpload}
            onRemove={(file) =>
              setFileList((prev) => prev.filter((f) => f.uid !== file.uid))
            }
          >
            <Button
              icon={<PaperClipOutlined />}
              disabled={fileList.length >= 3}
            >
              Attach File
            </Button>
          </Upload>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Accepted: JPG, PNG, PDF · Max 5MB each
          </Text>
        </div>
      )}

      {/* Notes */}
      <div style={{ marginTop: "1.5rem" }}>
        <label className="form-label">
          Notes <span className="form-span">*</span>
        </label>
        <Input
          placeholder="Notes"
          value={notes}
          data-testid="leave_notes"
          onChange={(e) => {
            const val = e.target.value;
            if (val.length <= 50) setNotes(val);
          }}
          onKeyDown={(e) => {
            if (
              notes &&
              notes.toString().length >= 50 &&
              !["Backspace", "Delete", "ArrowLeft", "ArrowRight"].includes(e.key)
            ) {
              e.preventDefault();
            }
          }}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {notes.length}/50 characters
        </Text>
      </div>

      {/* Buttons */}
      <div className="attendance-buttons">
        <Button onClick={onClose}>Discard</Button>
        <Button
          type="primary"
          disabled={isDisabled || !fromDate || !toDate || !leaveType || !notes}
          onClick={handleSave}
          data-testid="leave_save_button"
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default LeaveModal;